import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { HttpStatusCode } from "axios";

import { KeyValue } from "../../../../../types/keyValue";

import useErrors from "../../../../../libs/useErrors";
import {
  RentalItem,
  RentalStockGoodsTypeId,
  StoreInfo,
  createRentalStockRequest,
  getKeyValueFromRentalStockGoodsTypeId,
  getRentalItemList,
  getStoreList,
} from "../../../../../services/rentalStockServices";

import useFullScreenLoading from "../../../../../hooks/useFullScreenLoading";
import useSimplePopup from "../../../../../hooks/useSimplePopup";

import StickyHeader from "../../../../../components/Layout/StickyHeader";
import BottomButtonGroup from "../../../../../components/Button/Bottom";
import CommonSelect from "../../../../../components/Select";
import CommonLabel from "../../../../../components/Label";
import CommonInput from "../../../../../components/Input/CommonInput";
import RadioButtonGroup from "../../../../../components/Input/RadioButtonGroup";
import SimplePopup from "../../../../../components/Popup/Simple";

type Props = {};

export default function RentalStockCreate(props: Props) {
  const storeRef = useRef<HTMLDivElement>(null);
  const rentalItemRef = useRef<HTMLDivElement>(null);
  const stockCountRef = useRef<HTMLInputElement>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const loadingComponent = useFullScreenLoading({ isLoading });
  const navigate = useNavigate();
  const [simplePopupComponent, { open }] = useSimplePopup({});
  const [errors, { setError, clearErrors }] = useErrors();
  const [popup, setPopup] = useState<string>("");
  const location = useLocation();

  const [storeList, setStoreList] = useState<StoreInfo[]>([]);
  const [rentalItemList, setRentalItemList] = useState<RentalItem[]>([]);

  const [selectedStore, setSelectedStore] = useState<StoreInfo>();
  const [selectedGoodsTypeId, setSelectedGoodsTypeId] =
    useState<RentalStockGoodsTypeId>("UMBRELLA");
  const [selectedRentalItem, setSelectedRentalItem] = useState<RentalItem>();

  const selectedUseStockItemOption = [
    { key: "true", value: "필요" },
    { key: "false", value: "불필요(일회용품)" },
  ];
  const [selectedUseStockItem, setSelectedUseStockItem] = useState<KeyValue>(
    selectedUseStockItemOption[0]
  );
  const [stockCount, setStockCount] = useState<number>(0);

  const loadStoreList = async () => {
    setIsLoading(true);
    try {
      const result = await getStoreList();
      if (result.status === HttpStatusCode.Ok) {
        if (result.data.items && result.data.items.length > 0) {
          setStoreList(result.data.items);
          setSelectedStore(result.data.items[0]);
        } else {
          setStoreList([]);
          setSelectedStore(undefined);
          setError("store", "연결 할 수 있는 대여 장소가 없습니다.");
        }

        if (location.state && location.state.storeId) {
          const target = result.data.items.find(
            (item) => item.storeId === location.state.storeId
          );
          if (target) {
            setSelectedStore(target);
          }
        }
      }
    } catch (e) {
      console.log(e);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  };

  const loadRentalItemList = async () => {
    if (!selectedStore) return;

    setIsLoading(true);
    try {
      const result = await getRentalItemList({
        goodsTypeId: selectedGoodsTypeId,
        storeId: selectedStore?.storeId,
      });
      if (result.status === HttpStatusCode.Ok) {
        if (result.data.items && result.data.items.length > 0) {
          setRentalItemList(result.data.items);
          setSelectedRentalItem(result.data.items[0]);
        } else {
          setRentalItemList([]);
          setSelectedRentalItem(undefined);
          setError("rentalItem", "연결 할 수 있는 물품이 없습니다.");
        }
        setStockCount(0);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  };

  const validation = (error?: boolean) => {
    if (!selectedStore) {
      if (error) {
        if (storeList && storeList.length > 0)
          setError("store", "대여 장소를 선택해주세요.");
        else setError("store", "연결 할 수 있는 대여 장소가 없습니다.");
        storeRef.current?.focus();
      }
      return false;
    }
    if (!selectedRentalItem) {
      if (error) {
        if (rentalItemList && rentalItemList.length > 0)
          setError("rentalItem", "물품을 선택해주세요.");
        else setError("rentalItem", "연결 할 수 있는 물품이 없습니다.");
        rentalItemRef.current?.focus();
      }
      return false;
    }

    if (selectedUseStockItem.key === "true") {
      if (!stockCountRef.current?.value || stockCount === 0) {
        if (error) {
          setError("stockCount", "재고 수를 입력해주세요.");
          stockCountRef.current?.focus();
        }
        return false;
      }
    }

    return true;
  };

  const handleConfirm = async () => {
    if (!validation(true)) return;

    if (!selectedStore || !selectedRentalItem) return;

    setIsLoading(true);
    try {
      const result = await createRentalStockRequest({
        goodsId: selectedRentalItem.goodsId,
        storeId: selectedStore.storeId,
        useStock: selectedUseStockItem.key === "true",
        stockCount: Number(stockCountRef.current?.value),
        isActive: true,
      });

      if (result.status === HttpStatusCode.Ok) {
        setPopup("success");
      }
    } catch (e) {
      console.log(e);
      open("연결 실패 하였습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    clearErrors();
    loadStoreList();
  }, []);

  useEffect(() => {
    clearErrors();
    loadRentalItemList();
  }, [selectedGoodsTypeId, selectedStore]);

  return (
    <>
      {loadingComponent}
      <div className="px-5">
        <StickyHeader
          title="대여 물품 연결"
          onBackClick={() =>
            navigate("/manager/rental-manager/rental-stock/connect")
          }
        />
        <div className="flex flex-col pt-[30px] gap-5 pb-5">
          <div className="flex flex-col gap-2.5">
            <CommonLabel label="대여 장소" required />
            <CommonSelect
              className="h-bottom-button"
              useTruncate
              value={selectedStore?.storeId}
              optionList={
                storeList?.map((store) => ({
                  value: store.storeId,
                  label: store.storeName,
                })) ?? []
              }
              onChange={(target) => {
                clearErrors();
                const selectedStore = storeList.find(
                  (store) => store.storeId === target.value
                );
                if (!selectedStore) return;
                setSelectedStore(selectedStore);
                setSelectedGoodsTypeId(
                  getKeyValueFromRentalStockGoodsTypeId()[0]
                    .value as RentalStockGoodsTypeId
                );
              }}
              optionOrder="between"
              error={errors["store"]}
              ref={storeRef}
            />
          </div>
          <div className="flex flex-col gap-2.5">
            <CommonLabel label="물품 종류" required />
            <CommonSelect
              className="h-bottom-button"
              value={selectedGoodsTypeId}
              optionList={getKeyValueFromRentalStockGoodsTypeId() ?? []}
              onChange={(target) => {
                clearErrors();
                const selectedGoodsTypeId =
                  target.value as RentalStockGoodsTypeId;
                setSelectedGoodsTypeId(selectedGoodsTypeId);
              }}
              optionOrder="between"
            />
          </div>
          <div className="flex flex-col gap-2.5">
            <CommonLabel label="물품 명" required />
            <CommonSelect
              className="h-bottom-button"
              value={selectedRentalItem?.goodsId}
              optionList={
                rentalItemList?.map((item) => ({
                  value: item.goodsId,
                  label: item.goodsName,
                })) ?? []
              }
              onChange={(target) => {
                clearErrors();
                const selectedRentalItem = rentalItemList.find(
                  (item) => item.goodsId === target.value
                );
                if (!selectedRentalItem) return;
                setSelectedRentalItem(selectedRentalItem);
              }}
              error={errors["rentalItem"]}
              ref={rentalItemRef}
              optionOrder="between"
            />
          </div>
          <div className="flex flex-col gap-2.5">
            <CommonLabel label="재고 유무" required />
            <RadioButtonGroup
              variantActive="select"
              className="w-full text-sm font-normal leading-[26px] tracking-[0.28px]"
              items={selectedUseStockItemOption}
              value={selectedUseStockItem}
              onChange={(selectedItem) => {
                clearErrors();
                setStockCount(0);
                setSelectedUseStockItem(selectedItem);
              }}
            />
          </div>
          {selectedUseStockItem.key === "true" && (
            <div className="flex flex-col gap-2.5">
              <CommonLabel label="등록 재고 수" required />
              <CommonInput
                value={stockCount === 0 ? "" : stockCount.toString()}
                type="number"
                pattern="[0-9]*"
                min={1}
                max={999}
                maxLength={3}
                placeholder="초기 등록할 재고의 개수를 입력해 주세요"
                onChange={(value) => {
                  clearErrors();
                  setStockCount(Number(value));
                }}
                error={errors["stockCount"]}
                ref={stockCountRef}
              />
            </div>
          )}
        </div>
        <BottomButtonGroup
          title="등록 완료"
          validation={!validation()}
          onClick={handleConfirm}
        />
      </div>
      <SimplePopup
        isOpen={popup === "success"}
        onClose={() => {
          navigate("/manager/rental-manager/rental-stock/connect");
          setPopup("");
        }}
        message={"대여 물품 연결 성공했습니다."}
      />
      {simplePopupComponent}
    </>
  );
}
